import { useCallback, useSyncExternalStore } from 'react'
import { api } from './api'

/**
 * Per-user, per-browser preferences for spaces — favorited and hidden lists.
 * Lives in localStorage so it survives reloads without backend tables. We
 * scope by the current user id so multiple accounts on the same browser
 * don't bleed into each other.
 *
 * A small module-level store fans out updates across all hook instances so
 * toggling Favorites in one component (e.g. SpaceRowActions) re-renders
 * every other consumer (e.g. the sidebar's Favorites section) immediately —
 * without that, only a refresh would surface the change.
 */

function makeKey(userId, scope) {
  return `taskmagix.space.${scope}.${userId ?? 'anon'}`
}

function readSet(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((v) => typeof v === 'string'))
    }
  } catch {
    /* ignore */
  }
  return new Set()
}

function writeSet(key, set) {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set)))
  } catch {
    /* ignore */
  }
}

const entries = new Map()

function getEntry(key) {
  let entry = entries.get(key)
  if (!entry) {
    entry = { set: readSet(key), listeners: new Set() }
    entries.set(key, entry)
  }
  return entry
}

function subscribe(key, listener) {
  const entry = getEntry(key)
  entry.listeners.add(listener)
  return () => entry.listeners.delete(listener)
}

function getSnapshot(key) {
  return getEntry(key).set
}

function update(key, next) {
  const entry = getEntry(key)
  // Replace the reference so React's identity check picks up the change.
  entry.set = next
  writeSet(key, next)
  entry.listeners.forEach((l) => l())
  maybeScheduleSync(key)
}

function useStringSet(key) {
  // useSyncExternalStore subscribes to the shared store so every hook
  // instance for the same key re-renders when any of them mutates.
  const ids = useSyncExternalStore(
    (l) => subscribe(key, l),
    () => getSnapshot(key),
    () => getSnapshot(key),
  )

  const has = useCallback((id) => ids.has(id), [ids])
  const toggle = useCallback(
    (id) => {
      const next = new Set(ids)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      update(key, next)
    },
    [ids, key],
  )
  const add = useCallback(
    (id) => {
      if (ids.has(id)) return
      const next = new Set(ids)
      next.add(id)
      update(key, next)
    },
    [ids, key],
  )
  const remove = useCallback(
    (id) => {
      if (!ids.has(id)) return
      const next = new Set(ids)
      next.delete(id)
      update(key, next)
    },
    [ids, key],
  )

  return { ids, has, toggle, add, remove }
}

export function useFavoriteSpaces(userId) {
  return useStringSet(makeKey(userId, 'fav'))
}

export function useHiddenSpaces(userId) {
  return useStringSet(makeKey(userId, 'hidden'))
}

/** Per-user favourited Doc/Spreadsheet/Whiteboard ids (localStorage). */
export function useFavoriteDocuments(userId) {
  return useStringSet(makeKey(userId, 'doc-fav'))
}

/**
 * Per-user favourited LIST ids (localStorage).
 *
 * Stored as a string-set on a separate key from spaces. Without a
 * dedicated set, the previous behaviour was that the star button at
 * the top of the SpacePage always toggled the parent SPACE's
 * favourite — even when the URL pointed at `/app/spaces/<sid>/lists/
 * <lid>`. Users on a specific list expected only that list to land
 * in Favourites, not the whole space it lived in. This hook lets the
 * caller switch on whether `listId` is present and pick the right
 * favourites collection without touching the spaces set.
 *
 * Pair with `useFavoriteListMeta` below — the Set only carries ids,
 * so the sidebar would otherwise have no way to render a friendly
 * name + a working `/app/spaces/<sid>/lists/<lid>` link for a
 * favourited list. The meta JSON stores `{ [listId]: { name,
 * spaceId } }` and is written alongside every favourite toggle so
 * the sidebar can render the row without firing a fresh fetch.
 */
export function useFavoriteLists(userId) {
  return useStringSet(makeKey(userId, 'list-fav'))
}

/**
 * Per-user metadata for favourited lists — name + parent spaceId,
 * keyed by listId. Lets the Favourites sidebar render a working
 * label + link for a favourited list without fetching every space's
 * list collection on mount. Writers (SpacePage's star button) MUST
 * update this map at the same time they toggle the list-fav set;
 * the sidebar treats a missing meta entry as "loading" and hides
 * the row rather than render a broken link.
 */
export function useFavoriteListMeta(userId) {
  return useJsonObject(makeKey(userId, 'list-fav-meta'), {})
}

/**
 * Imperatively refresh a favourited list's stored label after it is renamed.
 *
 * The Favourites sidebar renders the name SNAPSHOT captured in
 * `list-fav-meta` at favouriting time (so it needn't fetch every space's
 * lists). Nothing else keeps that snapshot in step with the live list, so a
 * rename would otherwise leave the old name pinned in Favourites forever.
 * Call this from the rename success handler.
 *
 * It is deliberately conservative — it rewrites the entry ONLY when:
 *   • the list is actually favourited (a meta entry exists), and
 *   • the stored label still equals the OLD name.
 * The second guard protects a favourite whose label is the parent Project's
 * name (the star stores the page heading, which on a Project page is the
 * Project, not the raw list name): renaming the list must not overwrite that.
 * Writes go through the shared store, so every open `useFavoriteListMeta`
 * consumer (the sidebar) re-renders immediately.
 */
export function renameFavoriteListMeta(userId, listId, oldName, newName) {
  if (!listId || !newName) return
  const key = makeKey(userId, 'list-fav-meta')
  const current = getObjectSnapshot(key, {})
  const entry = current[listId]
  if (!entry) return // not favourited — nothing to refresh
  if (entry.name !== oldName) return // label is something else (e.g. Project name)
  if (entry.name === newName) return // already current — skip the write
  updateObject(key, {}, { ...current, [listId]: { ...entry, name: newName } })
}

/**
 * Refresh favourited-list labels after their parent Project (folder) is
 * renamed. When a favourited list lives inside a Project, the star stores the
 * PROJECT's name as the label (see the write in SpacePage), so a Project
 * rename must cascade to every such favourite or they keep the old name.
 *
 * A favourite is matched to the renamed Project by:
 *   • `folderId` — precise, for favourites pinned once the meta started
 *     recording it; or
 *   • a legacy fallback — favourites with no stored `folderId` whose label
 *     still equals the old Project name within the same Space.
 * Matched entries have their label updated (and back-filled with `folderId`).
 */
export function renameFavoriteFolderMeta(userId, folderId, spaceId, oldName, newName) {
  if (!folderId || !newName || newName === oldName) return
  const key = makeKey(userId, 'list-fav-meta')
  const current = getObjectSnapshot(key, {})
  let changed = false
  const next = {}
  for (const [listId, entry] of Object.entries(current)) {
    const matchesFolder = entry.folderId != null && entry.folderId === folderId
    const matchesLegacy =
      entry.folderId == null &&
      entry.spaceId === spaceId &&
      entry.name === oldName
    if ((matchesFolder || matchesLegacy) && entry.name !== newName) {
      next[listId] = { ...entry, name: newName, folderId }
      changed = true
    } else {
      next[listId] = entry
    }
  }
  if (changed) updateObject(key, {}, next)
}

/**
 * Sidebar collapse state. Stores *collapsed* ids so unknown/new entries
 * default to expanded — "open by default" behaviour.
 */
export function useCollapsedSpaces(userId) {
  return useStringSet(makeKey(userId, 'collapsed-space'))
}

export function useCollapsedFolders(userId) {
  return useStringSet(makeKey(userId, 'collapsed-folder'))
}

/**
 * User-panel Spaces accordion — the SINGLE space currently expanded in the
 * sidebar, or null = all collapsed. Stored per-user in localStorage so it
 * survives reloads. Only one space opens at a time; the admin sidebar keeps
 * its own independent multi-open collapse set (useCollapsedSpaces).
 * Returns `[expandedId, setExpanded]`.
 */
const EXPANDED_SPACE_DEFAULT = Object.freeze({ id: null })
export function useExpandedSpace(userId) {
  const [value, setValue] = useJsonObject(
    makeKey(userId, 'expanded-space'),
    EXPANDED_SPACE_DEFAULT,
  )
  const setExpanded = useCallback(
    (id) => setValue({ id: id ?? null }),
    [setValue],
  )
  return [value.id ?? null, setExpanded]
}

/**
 * User-panel Projects (folders) accordion — the SINGLE project/folder
 * currently expanded in the sidebar, or null = all collapsed. Mirrors
 * useExpandedSpace: only one project opens at a time and everything starts
 * CLOSED (folders no longer default to expanded). One id globally is enough
 * because the Space accordion already limits the tree to a single open Space.
 * Returns `[expandedId, setExpanded]`.
 */
const EXPANDED_FOLDER_DEFAULT = Object.freeze({ id: null })
export function useExpandedFolder(userId) {
  const [value, setValue] = useJsonObject(
    makeKey(userId, 'expanded-folder'),
    EXPANDED_FOLDER_DEFAULT,
  )
  const setExpanded = useCallback(
    (id) => setValue({ id: id ?? null }),
    [setValue],
  )
  return [value.id ?? null, setExpanded]
}

// ---------------------------------------------------------------------------
// JSON-object persistence — same shared-store pattern as the Set-backed hooks
// above, just for plain objects. Used today by `useTaskColumns` so the user's
// column-visibility choice on the Tasks list survives reloads + tab switches.
// ---------------------------------------------------------------------------

const objectEntries = new Map()

function readObject(key, defaults) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return { ...defaults }
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      // Merge with defaults so a newly-added column key (e.g. we ship
      // a new column in code) lands with its default value rather than
      // being undefined for users with stale localStorage.
      return { ...defaults, ...parsed }
    }
  } catch {
    /* ignore */
  }
  return { ...defaults }
}

function writeObject(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* ignore */
  }
}

function getObjectEntry(key, defaults) {
  let entry = objectEntries.get(key)
  if (!entry) {
    entry = { value: readObject(key, defaults), listeners: new Set(), defaults }
    objectEntries.set(key, entry)
  }
  return entry
}

function subscribeObject(key, defaults, listener) {
  const entry = getObjectEntry(key, defaults)
  entry.listeners.add(listener)
  return () => entry.listeners.delete(listener)
}

function getObjectSnapshot(key, defaults) {
  return getObjectEntry(key, defaults).value
}

function updateObject(key, defaults, next) {
  const entry = getObjectEntry(key, defaults)
  entry.value = next
  writeObject(key, next)
  entry.listeners.forEach((l) => l())
  maybeScheduleSync(key)
}

function useJsonObject(key, defaults) {
  const value = useSyncExternalStore(
    (l) => subscribeObject(key, defaults, l),
    () => getObjectSnapshot(key, defaults),
    () => getObjectSnapshot(key, defaults),
  )
  const setValue = useCallback(
    (nextOrUpdater) => {
      const current = getObjectSnapshot(key, defaults)
      const next =
        typeof nextOrUpdater === 'function'
          ? nextOrUpdater(current)
          : nextOrUpdater
      updateObject(key, defaults, next)
    },
    [key, defaults],
  )
  return [value, setValue]
}

/** Persisted column-visibility for the Tasks list view. Defaults match
 *  the original hardcoded shape — Assignee / Due date / Priority on,
 *  everything else off — so new users see what they used to. */
export function useTaskColumns(userId) {
  const defaults = TASK_COLUMNS_DEFAULTS
  return useJsonObject(makeKey(userId, 'task-columns'), defaults)
}

const TASK_COLUMNS_DEFAULTS = Object.freeze({
  // Status — hidden by default. Tasks are already grouped by
  // status, so the column is redundant for the common "browse by
  // status" view. Power users who regroup (by assignee / priority
  // / etc.) can opt in via Customize. Flipped from `true` so the
  // default Shown set matches the product-requested list:
  // Task Name, Assignee, Start date, Due date, Priority, Comments,
  // Files & Links. Existing users keep their stored preference;
  // only fresh accounts inherit this new default.
  status: false,
  assignee: true,
  // Start date is visible by default — most teams want to see both
  // ends of the task window at a glance.
  startDate: true,
  // Start time / due time stay hidden by default — they're a
  // power-user toggle for teams that schedule to the hour. When
  // enabled in Customize they slot in right AFTER their respective
  // date column (see COLUMN_ORDER in SpacePage.jsx).
  startTime: false,
  dueDate: true,
  dueTime: false,
  priority: true,
  // Comments — visible by default per product call. Lets users see
  // unread / total comment count without clicking into each task.
  comments: true,
  // Created by — visible by default so the task author is identifiable
  // at a glance. Toggleable via Customize (hide it there if not needed).
  createdBy: true,
  dateCreated: false,
  dateUpdated: false,
  dateDone: false,
  taskId: false,
  // Files & Links — per-task attachments column. Visible by default
  // per product call so the quick "drop a reference file / external
  // link" action is always one click away without opening the
  // detail dialog. The column lives at the tail of COLUMN_ORDER so
  // its presence never reflows the other columns.
  attachments: true,
  // Repeat — recurrence indicator column. Visible by default so teams can
  // spot which tasks auto-generate on a schedule at a glance. Read-only;
  // the schedule itself is edited from the task detail's Repeat row.
  recurring: true,
  // Proof required — standalone toggle column (block completion until proof is
  // attached). Off by default; opt-in via Customise.
  proof: false,
  // Accountability — the checker + verification workflow (status chip,
  // Submit / Approve / Reject) inline in the row. Off by default (opt-in via
  // Customize) since it's only relevant to teams using task verification.
  accountability: false,
})

/**
 * Persisted List/Board filter, keyed per user AND per list so it survives
 * a page refresh and each list keeps its own filter. Stored as plain
 * arrays (Sets aren't JSON-serialisable); the SpacePage bridges them back
 * to the Set-based filter API. The free-text search box is intentionally
 * NOT persisted here — it's a transient, always-visible control, not a
 * saved view setting. Keyed on listId so switching lists loads that
 * list's filter (the key change makes useJsonObject re-read).
 */
export function useTaskFilters(userId, listId) {
  return useJsonObject(
    makeKey(userId, `task-filters.${listId ?? 'none'}`),
    TASK_FILTERS_DEFAULTS,
  )
}

const TASK_FILTERS_DEFAULTS = Object.freeze({
  assignees: [],
  priorities: [],
  dueRange: 'all',
})

/**
 * LEGACY — superseded by the account-level `textSize` preference in
 * `display-prefs.js` (the header menu and the My Preferences page both use
 * that now). Kept only so the one-time migration can read the old
 * browser-only value (`taskmagix.space.font-size.<userId>`); nothing writes it.
 *
 * Per-user font-size preference (accessibility control in the header).
 *
 * Three presets — `compact` / `medium` / `large` — map to the
 * `data-font-size` attribute on `<html>`, which the global stylesheet
 * picks up to scale the `--text-*` token rung up or down. Storing the
 * choice per user (rather than globally on the document) keeps two
 * accounts on the same browser from clobbering each other's setting.
 *
 * Default is `medium` — what every existing user implicitly used
 * before this preference existed, so nobody's UI jumps after the
 * feature ships.
 *
 * The wiring lives in `App.jsx` (top-level effect that reads the hook
 * and mirrors the value to `document.documentElement.dataset
 * .fontSize`) so the rest of the app stays free of font-size logic.
 */
export function useFontSize(userId) {
  return useJsonObject(makeKey(userId, 'font-size'), FONT_SIZE_DEFAULTS)
}

const FONT_SIZE_DEFAULTS = Object.freeze({ level: 'medium' })

/** Persisted column-visibility for the Gantt view. The Gantt has its
 *  own defaults — the timeline carries most of the date / range
 *  information visually, so the left-pane defaults to a minimal set
 *  (Due date only) and admins opt in to extra columns via the
 *  Customize popover. Stored under a separate localStorage key so
 *  the Gantt's choices don't bleed into the List view's table and
 *  vice-versa. */
export function useGanttColumns(userId) {
  return useJsonObject(makeKey(userId, 'gantt-columns'), GANTT_COLUMNS_DEFAULTS)
}

const GANTT_COLUMNS_DEFAULTS = Object.freeze({
  // Only Due date renders on the left pane by default. Everything
  // else (Start date, Assignee, Priority, …) is opt-in via the
  // Customize popover — the bar in the right pane already carries
  // start/end and assignee colour cues, so the left pane stays
  // tight by default.
  assignee: false,
  startDate: false,
  startTime: false,
  dueDate: true,
  dueTime: false,
  priority: false,
  // Repeat — recurrence column on the Gantt left pane. On by default so the
  // recurring indicator/control is visible; toggle off via Customize.
  recurring: true,
  comments: false,
  dateCreated: false,
  dateUpdated: false,
  dateDone: false,
  taskId: false,
})

// Cross-tab sync — the storage event fires only for *other* tabs, so we
// reload the affected key into the store and notify in-page listeners.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (!e.key || !e.key.startsWith('taskmagix.space.')) return
    const entry = entries.get(e.key)
    if (entry) {
      entry.set = readSet(e.key)
      entry.listeners.forEach((l) => l())
    }
    const objectEntry = objectEntries.get(e.key)
    if (objectEntry) {
      objectEntry.value = readObject(e.key, objectEntry.defaults)
      objectEntry.listeners.forEach((l) => l())
    }
  })
}

export const __spacePrefsInternals = { entries, subscribe, getSnapshot, update }

// ---------------------------------------------------------------------------
// Cross-device sync (favourite Spaces / Lists + hidden Spaces).
//
// These few preferences follow the user between phone and desktop via
// `GET/PUT /me/preferences`; everything else above (columns, filters, font
// size, collapse/expand) is deliberately device-specific and stays local.
// localStorage remains a write-through cache so the UI is instant and works
// offline, while the SERVER is the source of truth on load. The hooks above
// are untouched — they read the same store, which this layer hydrates and
// mirrors upward.
// ---------------------------------------------------------------------------

const FAV_SPACES_SCOPE = 'fav'
const FAV_LISTS_SCOPE = 'list-fav'
const FAV_LIST_META_SCOPE = 'list-fav-meta'
const HIDDEN_SCOPE = 'hidden'

let syncUserId = null
let syncedKeys = new Set()
let hydrating = false // suppress upward writes while applying a server snapshot
let syncTimer = null
let syncPending = false

function scopedSyncKeys(userId) {
  return [FAV_SPACES_SCOPE, FAV_LISTS_SCOPE, FAV_LIST_META_SCOPE, HIDDEN_SCOPE].map(
    (s) => makeKey(userId, s),
  )
}

/** Called by update()/updateObject() after every store write. Debounces a PUT
 *  when a SYNCED key changed and we aren't mid-hydration. Rapid toggles
 *  coalesce into a single request carrying the final state. */
function maybeScheduleSync(key) {
  if (hydrating || !syncUserId || !syncedKeys.has(key)) return
  syncPending = true
  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = setTimeout(() => {
    syncTimer = null
    void pushSyncedToServer()
  }, 500)
}

/** True while a local change is queued or in flight — lets the focus
 *  re-hydrate skip so it never clobbers an unsynced change. */
export function hasPendingPreferenceSync() {
  return syncPending || syncTimer !== null
}

/** Build the server payload from the current store snapshot. Favourite-list
 *  META is emitted per favourited id (so a just-pinned list round-trips even
 *  before its label is known). */
function readSyncedSnapshot(userId) {
  const favouriteSpaceIds = [...getSnapshot(makeKey(userId, FAV_SPACES_SCOPE))]
  const favListIds = [...getSnapshot(makeKey(userId, FAV_LISTS_SCOPE))]
  const meta = getObjectSnapshot(makeKey(userId, FAV_LIST_META_SCOPE), {})
  const favouriteListMeta = favListIds.map((listId) => {
    const m = meta[listId] ?? {}
    return {
      listId,
      spaceId: m.spaceId ?? null,
      name: m.name ?? '',
      spaceName: m.spaceName ?? null,
      folderId: m.folderId ?? null,
    }
  })
  const hiddenSpaceIds = [...getSnapshot(makeKey(userId, HIDDEN_SCOPE))]
  return { favouriteSpaceIds, favouriteListMeta, hiddenSpaceIds }
}

async function pushSyncedToServer() {
  if (!syncUserId) return
  try {
    await api.put('/me/preferences', readSyncedSnapshot(syncUserId))
    syncPending = false
  } catch {
    // Leave syncPending set so the next successful write reconciles; a failed
    // sync must never surface an error or lose the local change.
  }
}

/** Apply a server snapshot into the store WITHOUT echoing it back up. */
function applyServerSnapshot(userId, data) {
  hydrating = true
  try {
    update(makeKey(userId, FAV_SPACES_SCOPE), new Set(data?.favouriteSpaceIds ?? []))
    const meta = {}
    const ids = []
    for (const m of data?.favouriteListMeta ?? []) {
      if (!m?.listId) continue
      ids.push(m.listId)
      meta[m.listId] = {
        name: m.name ?? '',
        spaceId: m.spaceId ?? null,
        spaceName: m.spaceName ?? null,
        folderId: m.folderId ?? null,
      }
    }
    update(makeKey(userId, FAV_LISTS_SCOPE), new Set(ids))
    updateObject(makeKey(userId, FAV_LIST_META_SCOPE), {}, meta)
    update(makeKey(userId, HIDDEN_SCOPE), new Set(data?.hiddenSpaceIds ?? []))
  } finally {
    hydrating = false
  }
}

const serverPreferenceListeners = new Set()

/** Subscribe to every successful `GET /me/preferences` snapshot
 *  (`fn(userId, data, startedAt)` — startedAt = when the GET was sent). Used by the display-preferences store so the one
 *  request serves both. Returns an unsubscribe function. */
export function onServerPreferences(fn) {
  serverPreferenceListeners.add(fn)
  return () => serverPreferenceListeners.delete(fn)
}

/**
 * Load the signed-in user's synced preferences from the server into the
 * store. Call once the user id is known (App root) and again on tab focus.
 *
 * First-run migration: if the server has nothing yet but THIS browser already
 * holds favourites (a user from before sync existed), those are pushed UP so
 * the switch to server-backed sync loses nothing. Otherwise the server wins
 * and overwrites the local cache. On network error the cache is left as-is.
 */
export async function hydratePreferences(userId) {
  if (!userId) return
  syncUserId = userId
  syncedKeys = new Set(scopedSyncKeys(userId))
  let data
  const startedAt = Date.now()
  try {
    data = (await api.get('/me/preferences')).data
  } catch {
    return // offline / error — keep the localStorage cache
  }
  // Same response also carries the display & accessibility slice — hand it to
  // its own store (display-prefs.js) rather than fetching it a second time.
  // `startedAt` lets that store ignore a snapshot older than its own writes.
  for (const fn of serverPreferenceListeners) {
    try {
      void fn(userId, data, startedAt)
    } catch {
      /* a listener's failure must never block the favourites sync */
    }
  }
  const serverEmpty =
    (data?.favouriteSpaceIds?.length ?? 0) === 0 &&
    (data?.favouriteListMeta?.length ?? 0) === 0 &&
    (data?.hiddenSpaceIds?.length ?? 0) === 0
  const localHasData =
    getSnapshot(makeKey(userId, FAV_SPACES_SCOPE)).size > 0 ||
    getSnapshot(makeKey(userId, FAV_LISTS_SCOPE)).size > 0 ||
    getSnapshot(makeKey(userId, HIDDEN_SCOPE)).size > 0
  if (serverEmpty && localHasData) {
    await pushSyncedToServer() // one-time migration; keep local as-is
    return
  }
  applyServerSnapshot(userId, data)
}

/** Clear sync state on sign-out so the next account on this browser can't push
 *  the previous user's favourites. */
export function resetPreferencesSync() {
  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = null
  syncPending = false
  syncUserId = null
  syncedKeys = new Set()
}
